import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { BiometricAuthService } from '../../core/services/auth/biometric-auth.service';
import { PinAuthService } from '../../core/services/auth/pin-auth.service';
import { PinPadComponent } from '../pin-pad/pin-pad.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'angular-oauth2-oidc';

@Component({
  selector: 'rrp-pin-auth',
  templateUrl: './pin-auth.component.html',
  styleUrl: './pin-auth.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PinPadComponent, TranslateModule],
})
export class PinAuthComponent implements OnInit, OnDestroy {
  private readonly biometricService = inject(BiometricAuthService);
  private readonly pinAuthService = inject(PinAuthService);
  private readonly router = inject(Router);
  private readonly oauthService = inject(OAuthService);
  private readonly translateService = inject(TranslateService);

  protected readonly pinPad = viewChild<PinPadComponent>('pinPad');

  protected readonly errorKey = signal<string | undefined>(undefined);
  protected readonly isLocked = signal(false);
  protected readonly lockRemainingSeconds = signal(0);
  protected readonly canUseBiometric = signal(false);

  private lockTimerInterval: ReturnType<typeof setInterval> | null = null;

  public async ngOnInit(): Promise<void> {
    await this.checkLockStatus();
    await this.checkBiometricAvailability();
    await this.tryBiometricAuth();
  }

  public ngOnDestroy(): void {
    this.clearLockTimer();
  }

  private async checkLockStatus(): Promise<void> {
    const status = await this.pinAuthService.getLockStatus();

    if (status.isLocked) {
      this.isLocked.set(true);
      this.startLockTimer(status.remainingMs);
    }
  }

  private async checkBiometricAvailability(): Promise<void> {
    try {
      const [biometricEnabled, biometricStatus] = await Promise.all([
        this.pinAuthService.isBiometricEnabled(),
        this.biometricService.getStatus(),
      ]);

      this.canUseBiometric.set(
        biometricEnabled && biometricStatus.isStrongAvailable,
      );
    } catch {
      this.canUseBiometric.set(false);
    }
  }

  private async tryBiometricAuth(): Promise<void> {
    if (!this.canUseBiometric() || this.isLocked()) {
      return;
    }

    try {
      await this.biometricService.authenticate({
        reason: this.translateService.instant('pin.reason'),
        cancelTitle: this.translateService.instant('pin.cancel-title'),
      });

      this.pinAuthService.authenticateWithBiometric();
      await this.navigateToApp();
    } catch (error) {
      if (this.biometricService.isUserCancel(error)) {
        return;
      }

      if (this.biometricService.isBiometryLockout(error)) {
        this.errorKey.set('pin.biometric.error.lockout');
        return;
      }

      console.error('Biometric auth failed:', error);
    }
  }

  protected async onPinEntered(pin: string): Promise<void> {
    if (this.isLocked()) {
      return;
    }

    const result = await this.pinAuthService.verifyPin(pin);

    if (result.success) {
      await this.navigateToApp();
      return;
    }

    if (result.lockStatus.isLocked) {
      this.isLocked.set(true);
      this.startLockTimer(result.lockStatus.remainingMs);
      this.errorKey.set('pin.error.locked');
    } else {
      const attemptsLeft = this.getAttemptsBeforeLock(
        result.lockStatus.failedAttempts,
      );

      if (attemptsLeft <= 3 && attemptsLeft > 0) {
        //TODO: продумать передачу оставшегося кол-ва попыток
        this.errorKey.set('pin.error.wrong');
      } else {
        this.errorKey.set('pin.error.wrong');
      }
    }

    this.pinPad()?.reset();
  }

  protected async onBiometricClick(): Promise<void> {
    if (!this.canUseBiometric() || this.isLocked()) {
      return;
    }

    try {
      await this.biometricService.authenticate({
        reason: this.translateService.instant('pin.reason'),
        cancelTitle: this.translateService.instant('pin.cancel-title'),
      });

      this.pinAuthService.authenticateWithBiometric();
      await this.navigateToApp();
    } catch (error) {
      if (this.biometricService.isUserCancel(error)) {
        return;
      }

      if (this.biometricService.isBiometryLockout(error)) {
        this.errorKey.set('pin.biometric.error.lockout');
      }
    }
  }

  protected async onLogout(): Promise<void> {
    await this.pinAuthService.resetAll();
    this.oauthService.logOut();

    await this.router.navigate(['/login']);
  }

  private async navigateToApp(): Promise<void> {
    await this.router.navigate(['/']);
  }

  private getAttemptsBeforeLock(currentAttempts: number): number {
    if (currentAttempts < 5) {
      return 5 - currentAttempts;
    }
    if (currentAttempts < 10) {
      return 10 - currentAttempts;
    }
    return 0;
  }

  private startLockTimer(remainingMs: number): void {
    this.clearLockTimer();

    this.lockRemainingSeconds.set(Math.ceil(remainingMs / 1000));

    this.lockTimerInterval = setInterval(() => {
      const current = this.lockRemainingSeconds();

      if (current <= 1) {
        this.clearLockTimer();
        this.isLocked.set(false);
        this.errorKey.set(undefined);
        this.lockRemainingSeconds.set(0);
      } else {
        this.lockRemainingSeconds.set(current - 1);
      }
    }, 1000);
  }

  private clearLockTimer(): void {
    if (this.lockTimerInterval) {
      clearInterval(this.lockTimerInterval);
      this.lockTimerInterval = null;
    }
  }

  protected formatLockTime(): string {
    const seconds = this.lockRemainingSeconds();
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }

    return `${seconds}`;
  }

  protected getLockMessage(): string {
    return this.translateService.instant('pin.error.locked-time', {
      time: this.formatLockTime(),
    });
  }
}
